/**
 * Helper functions for store category management
 * Keeps the main component clean and organized
 */

/**
 * Get the depth of a category (1 = Store Category, 2 = Sub-Store, 3 = Grand-Child)
 * @param {string} categoryId - The category ID
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {number} Depth of the category
 */
export const getCategoryDepth = (categoryId, idToCat) => {
    let depth = 1;
    let current = idToCat.get(categoryId);
    while (current) {
        const p = current.parentId;
        if (!p) break;
        if (typeof p === 'string' && p.startsWith('sc:')) {
            depth += 1;
            current = idToCat.get(p.slice(3));
        } else {
            break;
        }
    }
    return depth;
};

/**
 * Get the depth of a parent value (for dropdown selection)
 * @param {string} value - Parent value (mp:option or sc:categoryId)
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {number} Depth that a child would have under this parent
 */
export const parentDepthForValue = (value, idToCat) => {
    if (!value) return 0;
    if (value.startsWith('mp:')) return 1;
    if (value.startsWith('sc:')) return getCategoryDepth(value.slice(3), idToCat);
    return 0;
};

/**
 * Extract store parent ID from parentId string
 * @param {string|null} parentId - Parent ID (mp:option, sc:categoryId, or null)
 * @returns {string|null} Store category ID or null
 */
export const getStoreParentId = (parentId) => {
    if (!parentId) return null;
    if (typeof parentId === 'string') {
        if (parentId.startsWith('sc:')) return parentId.slice(3);
        if (parentId.startsWith('mp:')) return null;
        return parentId;
    }
    return parentId || null;
};

/**
 * Get all ancestors of a category (from root to immediate parent)
 * @param {string} categoryId - The category ID
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {Array<string>} Array of ancestor category IDs (root first, immediate parent last)
 */
export const getAncestors = (categoryId, idToCat) => {
    const result = [];
    let current = idToCat.get(categoryId);
    let guard = 0;
    while (current && guard < 20) {
        const pId = getStoreParentId(current.parentId);
        if (!pId || !idToCat.has(pId)) break;
        result.push(pId);
        current = idToCat.get(pId);
        guard += 1;
    }
    return result.reverse();
};

/**
 * Check if a category is a descendant of another category
 * @param {string} maybeDescendantId - Potential descendant ID
 * @param {string} ancestorId - Potential ancestor ID
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {boolean} True if maybeDescendantId is a descendant of ancestorId
 */
export const isDescendant = (maybeDescendantId, ancestorId, idToCat) => {
    if (!maybeDescendantId || !ancestorId) return false;
    let current = idToCat.get(maybeDescendantId);
    let guard = 0;
    while (current && guard < 20) {
        const pId = getStoreParentId(current.parentId);
        if (!pId) return false;
        if (pId === ancestorId) return true;
        current = idToCat.get(pId);
        guard += 1;
    }
    return false;
};

/**
 * Get all descendants of a category (direct and indirect children)
 * @param {string} categoryId - The category ID
 * @param {Array} categories - All categories array
 * @returns {Array<string>} Array of descendant category IDs
 */
export const getAllDescendants = (categoryId, categories) => {
    const idToChildren = new Map();

    categories.forEach(c => {
        const p = c.parentId;
        if (typeof p === 'string' && p.startsWith('sc:')) {
            const pid = p.slice(3);
            const arr = idToChildren.get(pid) || [];
            arr.push(c.id);
            idToChildren.set(pid, arr);
        } else if (p === categoryId) {
            // Handle legacy format
            const arr = idToChildren.get(categoryId) || [];
            arr.push(c.id);
            idToChildren.set(categoryId, arr);
        }
    });

    const result = [];
    const stack = [categoryId];
    while (stack.length) {
        const id = stack.pop();
        const children = idToChildren.get(id) || [];
        result.push(...children);
        stack.push(...children);
    }
    return result;
};

/**
 * Get the relative depth of a descendant from its ancestor
 * @param {string} descendantId - The descendant category ID
 * @param {string} ancestorId - The ancestor category ID
 * @param {Array} categories - All categories array
 * @returns {number} Relative depth (0 = direct child, 1 = grandchild, etc.)
 */
export const getRelativeDepth = (descendantId, ancestorId, categories) => {
    const idToNode = new Map(categories.map(c => [c.id, c]));
    let depth = 0;
    let current = idToNode.get(descendantId);

    // Traverse up the tree from descendant to ancestor
    // Count how many levels we need to go up
    while (current && current.id !== ancestorId) {
        const p = current.parentId;
        if (!p) break;

        // Check if parent is the ancestor
        if (typeof p === 'string' && p.startsWith('sc:')) {
            const parentId = p.slice(3);
            if (parentId === ancestorId) {
                // Found ancestor, increment depth and stop
                depth++;
                break;
            }
            // Move up one level
            current = idToNode.get(parentId);
            depth++;
        } else if (p === ancestorId) {
            // Found ancestor (direct parent, not using sc: prefix)
            depth++;
            break;
        } else {
            // Parent is not a store category, stop
            break;
        }
    }
    return depth;
};

/**
 * Check if a category has any children (direct or indirect)
 * @param {string} categoryId - The category ID
 * @param {Array} categories - All categories array
 * @returns {boolean} True if category has any children
 */
export const hasChildren = (categoryId, categories) => {
    const descendants = getAllDescendants(categoryId, categories);
    return descendants.length > 0;
};

/**
 * Check if a category has grandchildren (descendants at relative depth >= 2)
 * @param {string} categoryId - The category ID
 * @param {Array} categories - All categories array
 * @returns {boolean} True if category has any grandchild
 */
export const hasGrandchildren = (categoryId, categories) => {
    const descendants = getAllDescendants(categoryId, categories);
    for (const descId of descendants) {
        const relativeDepth = getRelativeDepth(descId, categoryId, categories);
        if (relativeDepth >= 2) {
            return true; // Has grandchildren
        }
    }
    return false;
};

/**
 * Check if moving a category to a new parent would cause any descendant to exceed max depth (4)
 * @param {string} categoryId - The category being moved
 * @param {string} newParentId - The new parent ID (mp:option or sc:categoryId)
 * @param {Array} categories - All categories array
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {boolean} True if moving would cause any descendant to exceed depth 4
 */
export const wouldExceedMaxDepth = (categoryId, newParentId, categories, idToCat) => {
    const descendants = getAllDescendants(categoryId, categories);
    if (descendants.length === 0) return false; // No children, safe to move

    // Marketplace options are always safe (moved category becomes depth 2)
    if (newParentId.startsWith('mp:')) {
        // Check if any grandchild would exceed depth 4
        // Moved category depth = 2, so grandchild (relative depth 2) = 2 + 2 = 4 (valid)
        // But if there's a great-grandchild (relative depth 3) = 2 + 3 = 5 (invalid)
        for (const descId of descendants) {
            const relativeDepth = getRelativeDepth(descId, categoryId, categories);
            if (relativeDepth >= 3) {
                return true; // Would exceed max depth
            }
        }
        return false; // Marketplace is always safe for up to grandchildren
    }
    
    // Calculate new depth of the moved category
    const newParentDepth = parentDepthForValue(newParentId, idToCat);
    const newCategoryDepth = newParentDepth + 1;
    
    // Check each descendant
    for (const descId of descendants) {
        const relativeDepth = getRelativeDepth(descId, categoryId, categories);
        const newDescendantDepth = newCategoryDepth + relativeDepth;
        if (newDescendantDepth > 4) {
            return true; // Would exceed max depth
        }
    }
    
    return false;
};

/**
 * Check if reparenting children of a deleted category would cause depth violations
 * @param {string} categoryId - The category being deleted
 * @param {string} newParentId - The new parent ID for children (deleted category's parent)
 * @param {Array} categories - All categories array
 * @param {Map} idToCat - Map of category ID to category object
 * @returns {boolean} True if reparenting would cause any child to exceed depth 4
 */
export const wouldReparentingExceedDepth = (categoryId, newParentId, categories, idToCat) => {
    if (!newParentId) {
        // If no parent, children would become root (depth 1), which is always safe
        return false;
    }
    
    // Get direct children of the category being deleted
    const directChildren = categories.filter(c => {
        const p = c.parentId;
        return (p === `sc:${categoryId}` || p === categoryId);
    });
    
    if (directChildren.length === 0) return false; // No children, safe to delete
    
    // Check each direct child
    for (const child of directChildren) {
        // Calculate new depth of the child after reparenting
        const newParentDepth = parentDepthForValue(newParentId, idToCat);
        const newChildDepth = newParentId.startsWith('mp:') ? 2 : newParentDepth + 1;
        
        // Get all descendants of this child
        const childDescendants = getAllDescendants(child.id, categories);
        
        // Check if any descendant would exceed depth
        for (const descId of childDescendants) {
            const relativeDepth = getRelativeDepth(descId, child.id, categories);
            const newDescendantDepth = newChildDepth + relativeDepth;
            if (newDescendantDepth > 4) {
                return true; // Would exceed max depth
            }
        }
        
        // Also check the child itself
        if (newChildDepth > 4) {
            return true;
        }
    }
    
    return false;
};

